import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Context,
  DialogContext,
  DialogMaxWidth,
  DialogShellProps,
  DialogVariant,
} from './context';
import { useIsFullScreen } from './useIsFullScreen';

export interface RootHandlers {
  open: () => void;
  close: () => void;
}

export type BaseDialogProps = DialogShellProps & {
  width?: DialogMaxWidth;
  variant?: DialogVariant;
  children: ReactNode;
};

export type ControlledDialogProps = BaseDialogProps & {
  open?: boolean;
  onClose?: () => void;
};

export type RootProps = ControlledDialogProps;

let idCounter = 0;
const useId = () => {
  const idRef = useRef<string>();
  if (!idRef.current) {
    idRef.current = `id-${idCounter++}`;
  }
  return idRef.current;
};

const Root = forwardRef<RootHandlers, RootProps>((props, ref) => {
  const [openState, setOpenState] = useState(props.open ?? false);
  const id = useId();

  const {
    open = false,
    onClose,
    children,
    width,
    variant = 'default',
    ...dialogProps
  } = props;

  const handleOpen = useCallback(() => setOpenState(true), []);
  const handleClose = useCallback(() => {
    setOpenState(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    setOpenState(open);
  }, [open]);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpenState(true),
      close: () => setOpenState(false),
    }),
    [],
  );

  const effectiveWidth =
    width ?? (dialogProps.maxWidth === undefined ? 'xs' : undefined);

  const { isFullScreen, isFullScreenLayout } = useIsFullScreen(variant);

  const value: Context = useMemo(
    () => ({
      id,
      open: openState,
      variant,
      onClose: handleClose,
      onOpen: handleOpen,
      props: {
        ...dialogProps,
        maxWidth: dialogProps.maxWidth ?? effectiveWidth,
        fullWidth: dialogProps.fullWidth ?? (effectiveWidth ? true : undefined),
      },
      isLoading: false,
      isFullScreen,
      isFullScreenLayout,
    }),
    [
      openState,
      handleClose,
      handleOpen,
      dialogProps,
      id,
      effectiveWidth,
      variant,
      isFullScreen,
      isFullScreenLayout,
    ],
  );

  return (
    <DialogContext.Provider value={value}>{children}</DialogContext.Provider>
  );
});

Root.displayName = 'DialogComposite.Root';

export default Root;
